import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowLeftIcon,
  PencilSquareIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import quizService from '../services/quizService';
import { Quiz, QuizAttempt, QuizQuestion, QuestionType } from '../services/quizService';
import { toast } from 'react-hot-toast';
import { createSafeHtml, containsHtml } from '../utils/htmlUtils';

// Helper para normalizar texto removendo acentos e convertendo para minúsculas
const normalizeText = (text: string): string => {
  return text
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, ''); // Remove diacríticos/acentos
};

// Helper para verificar se duas respostas são equivalentes (ignorando acentos e capitalização)
const areAnswersEquivalent = (answer1: string, answer2: string): boolean => {
  return normalizeText(answer1) === normalizeText(answer2);
};

// Helper para obter o tipo de questão
const getQuestionType = (question: QuizQuestion): QuestionType => {
  return question.questionType || 'multiple_choice';
};

const QuizPlay: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [attempt, setAttempt] = useState<QuizAttempt | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [showFeedback, setShowFeedback] = useState<Record<number, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [shuffledOptions, setShuffledOptions] = useState<Record<number, string[]>>({});
  // Estado para campos de texto (preenchimento e dissertativa)
  const [textInputs, setTextInputs] = useState<Record<number, string>>({});

  useEffect(() => {
    if (id) {
      initializeQuiz(false);
    }
  }, [id]);



  const initializeQuiz = async (showToast: boolean = true) => {
    try {
      setLoading(true);
      
      // Reset all state
      setAnswers({});
      setShowFeedback({});
      setTextInputs({});
      setCurrentQuestionIndex(0);
      setSubmitting(false);
      
      const quizData = await quizService.getQuiz(id!);
      setQuiz(quizData);

      // Start a new quiz attempt
      const attemptData = await quizService.startQuizAttempt(id!);
      setAttempt(attemptData);
      
      // Pre-shuffle all question options to keep them stable (only for multiple choice and true/false)
      const shuffled: Record<number, string[]> = {};
      quizData.questions.forEach((question, index) => {
        const questionType = getQuestionType(question);
        if (questionType === 'multiple_choice') {
          const options = [question.correctAnswer, ...question.wrongAnswers];
          shuffled[index] = options.sort(() => Math.random() - 0.5);
        } else if (questionType === 'true_false') {
          // Para V/F, mantém a ordem padrão
          shuffled[index] = ['Verdadeiro', 'Falso'];
        }
      });
      setShuffledOptions(shuffled);
      
      if (showToast) {
        toast.success('Quiz reiniciado!');
      }
      
    } catch (error: any) {
      console.error('Error initializing quiz:', error);
      toast.error(error.response?.data?.message || 'Erro ao carregar quiz');
      navigate('/quizzes');
    } finally {
      setLoading(false);
    }
  };

  const getQuestionOptions = (questionIndex: number): string[] => {
    return shuffledOptions[questionIndex] || [];
  };

  const handleAnswerSelect = (questionIndex: number, selectedOption: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionIndex]: selectedOption
    }));
    setShowFeedback(prev => ({
      ...prev,
      [questionIndex]: true
    }));
  };

  // Handler para campos de texto (preenchimento e dissertativa)
  const handleTextInputChange = (questionIndex: number, text: string) => {
    setTextInputs(prev => ({
      ...prev,
      [questionIndex]: text
    }));
    // Atualiza as respostas também
    setAnswers(prev => ({
      ...prev,
      [questionIndex]: text
    }));
  };

  // Confirma resposta de texto (para mostrar feedback em questões de preenchimento)
  const handleTextSubmit = (questionIndex: number) => {
    if (textInputs[questionIndex]?.trim()) {
      setShowFeedback(prev => ({
        ...prev,
        [questionIndex]: true
      }));
    }
  };

  // Conta palavras em um texto
  const countWords = (text: string): number => {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < (quiz?.questions.length || 0) - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmitQuiz = async () => {
    if (!attempt || !quiz) return;

    try {
      setSubmitting(true);

      // Convert answers to the format expected by the backend
      const formattedAnswers = Object.entries(answers).map(([questionIndex, answer]) => {
        const question = quiz.questions[parseInt(questionIndex)];
        const questionId = question.id || `question-${parseInt(questionIndex) + 1}`;
        return {
          questionId: questionId,
          selectedAnswer: answer,
          timeSpent: 0 // Placeholder; per-question timing not yet tracked
        };
      });

      try {
        const result = await quizService.submitQuizAttempt(
          attempt._id,
          formattedAnswers,
          undefined
        );
        navigate(`/quiz/${id}/results`, { state: { result } });
      } catch (backendError: any) {
        console.error('Backend error:', backendError);
        console.error('Backend error response:', backendError.response?.data);
        console.warn('Backend not ready, using mock result');
        // Create mock result for demonstration
        const correctAnswersCount = Object.entries(answers).filter(([questionIndex, selectedAnswer]) => {
          const question = quiz.questions[parseInt(questionIndex)];
          return selectedAnswer === question.correctAnswer;
        }).length;

        const mockAnswers = Object.entries(answers).map(([questionIndex, selectedAnswer]) => {
          const question = quiz.questions[parseInt(questionIndex)];
          return {
            questionId: question.id || `question-${parseInt(questionIndex) + 1}`,
            selectedAnswer,
            isCorrect: selectedAnswer === question.correctAnswer
          };
        });

        const mockResult = {
          quiz: {
            _id: id!,
            title: quiz.title,
            description: quiz.description,
            questions: quiz.questions.map(q => ({
              id: q.id,
              question: q.question,
              correctAnswer: q.correctAnswer,
              explanation: q.explanation
            }))
          },
          attempt: {
            _id: attempt._id,
            score: Math.round((correctAnswersCount / quiz.questions.length) * 100),
            totalQuestions: quiz.questions.length,
            correctAnswers: correctAnswersCount,
            timeSpent: 0,
            answers: mockAnswers
          },
          summary: {
            score: Math.round((correctAnswersCount / quiz.questions.length) * 100),
            totalQuestions: quiz.questions.length,
            correctAnswers: correctAnswersCount,
            incorrectAnswers: quiz.questions.length - correctAnswersCount,
            timeSpent: 0,
            isNewBestScore: false
          }
        };

        navigate(`/quiz/${id}/results`, { state: { result: mockResult } });
      }
    } catch (error: any) {
      console.error('Error submitting quiz:', error);
      toast.error('Erro ao enviar quiz');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getAnsweredQuestionsCount = () => {
    return Object.keys(answers).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!quiz || !attempt) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            Quiz não encontrado
          </h2>
          <button
            onClick={() => navigate('/quizzes')}
          >
            Voltar aos Quizzes
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = quiz.questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / quiz.questions.length) * 100;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-5xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-5">
          {/* Main header row */}
          <div className="flex items-center justify-between gap-3 sm:gap-4">
            <button
              onClick={() => navigate('/quizzes')}
              className="p-3 sm:p-4 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0 h-12 sm:h-14"
            >
              <ArrowLeftIcon className="h-6 w-6 sm:h-6 sm:w-6 text-gray-600 dark:text-gray-400" />
            </button>
            
            {/* Stats section in center */}
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="flex items-center text-base sm:text-lg font-semibold bg-green-500/20 dark:bg-green-500/20 px-3 sm:px-4 rounded-lg h-12 sm:h-14">
                <CheckCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-1.5 text-green-600 dark:text-green-400" />
                <span className="text-gray-900 dark:text-white whitespace-nowrap">
                  {getAnsweredQuestionsCount()}/{quiz.questions.length}
                </span>
              </div>
            </div>

            <button
              onClick={() => initializeQuiz()}
              disabled={submitting}
              className="p-3 sm:p-4 rounded-lg text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0 h-12 sm:h-14"
              title="Reiniciar Quiz"
            >
              <ArrowPathIcon className="h-6 w-6 sm:h-6 sm:w-6" />
            </button>
          </div>
          
          {/* Title section below */}
          <div className="hidden md:block mt-4">
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100 text-center">
              {quiz.title}
            </h1>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-3 sm:mt-4">
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 sm:h-2">
              <div
                className="bg-purple-600 h-1.5 sm:h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Question Content */}
      <div className="max-w-5xl mx-auto px-1 sm:px-6 lg:px-8 py-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 border-l-[1px] border-r-[1px] sm:border-l sm:border-r">
          <div className="p-8">
            {/* Question */}
            <div className="mb-6 sm:mb-8">
              {containsHtml(currentQuestion.question) ? (
                <h2 
                  className="text-lg sm:text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-3 sm:mb-4 leading-relaxed"
                  dangerouslySetInnerHTML={createSafeHtml(currentQuestion.question)}
                />
              ) : (
                <h2 className="text-lg sm:text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-3 sm:mb-4 leading-relaxed">
                  {currentQuestion.question}
                </h2>
              )}
              
              {/* Badge do tipo de questão */}
              <div className="flex items-center gap-2 mb-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  getQuestionType(currentQuestion) === 'multiple_choice' 
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                    : getQuestionType(currentQuestion) === 'true_false'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                    : getQuestionType(currentQuestion) === 'fill_blank'
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                    : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                }`}>
                  {getQuestionType(currentQuestion) === 'multiple_choice' && 'Múltipla Escolha'}
                  {getQuestionType(currentQuestion) === 'true_false' && 'Verdadeiro ou Falso'}
                  {getQuestionType(currentQuestion) === 'fill_blank' && 'Preencher Lacuna'}
                  {getQuestionType(currentQuestion) === 'essay' && 'Dissertativa'}
                </span>
                {getQuestionType(currentQuestion) === 'essay' && currentQuestion.minWords && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    (Mínimo {currentQuestion.minWords} palavras)
                  </span>
                )}
              </div>
            </div>

            {/* Renderização baseada no tipo de questão */}
            {(getQuestionType(currentQuestion) === 'multiple_choice' || getQuestionType(currentQuestion) === 'true_false') && (
              /* Options para Múltipla Escolha e Verdadeiro/Falso */
              <div className="space-y-3 mb-8">
                {getQuestionOptions(currentQuestionIndex).map((option: string, index: number) => {
                  const optionLetter = String.fromCharCode(65 + index); // A, B, C, D
                  const isSelected = answers[currentQuestionIndex] === option;
                  const feedbackVisible = showFeedback[currentQuestionIndex];
                  const isCorrectAnswer = option === currentQuestion.correctAnswer;
                  const isCorrect = feedbackVisible && isSelected && isCorrectAnswer;
                  const isWrong = feedbackVisible && isSelected && !isCorrectAnswer;
                  const shouldHighlightCorrect = feedbackVisible && isCorrectAnswer && !isSelected;
                  
                  return (
                    <button
                      key={index}
                      onClick={() => handleAnswerSelect(currentQuestionIndex, option)}
                      disabled={feedbackVisible}
                      className={`w-full p-4 text-left rounded-lg border-2 transition-all duration-200 ${
                        isSelected
                          ? isCorrect
                            ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                            : isWrong
                              ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                              : 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                          : shouldHighlightCorrect
                            ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                      }`}
                    >
                      <div className="flex items-center">
                        <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium mr-4 ${
                          isSelected
                            ? isCorrect
                              ? 'bg-green-500 text-white'
                              : isWrong
                                ? 'bg-red-500 text-white'
                                : 'bg-purple-500 text-white'
                            : shouldHighlightCorrect
                              ? 'bg-green-500 text-white'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                        }`}>
                          {optionLetter}
                        </span>
                        {containsHtml(option) ? (
                          <span 
                            className={`${
                              isSelected || shouldHighlightCorrect
                                ? 'text-gray-900 dark:text-gray-100 font-medium'
                                : 'text-gray-700 dark:text-gray-300'
                            }`}
                            dangerouslySetInnerHTML={createSafeHtml(option)}
                          />
                        ) : (
                          <span className={`${
                            isSelected || shouldHighlightCorrect
                              ? 'text-gray-900 dark:text-gray-100 font-medium'
                              : 'text-gray-700 dark:text-gray-300'
                          }`}>
                            {option}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Preenchimento de Lacuna */}
            {getQuestionType(currentQuestion) === 'fill_blank' && (
              <div className="mb-6 sm:mb-8">
                {/* Texto com lacuna */}
                {currentQuestion.blankText && (
                  <div className="mb-4 p-3 sm:p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p className="text-base sm:text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                      {currentQuestion.blankText.split('___').map((part, idx, arr) => (
                        <React.Fragment key={idx}>
                          {part}
                          {idx < arr.length - 1 && (
                            <span className="inline-block min-w-[60px] sm:min-w-[100px] mx-0.5 sm:mx-1 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-yellow-100 dark:bg-yellow-900/30 border-b-2 border-yellow-500 text-center font-medium text-sm sm:text-base">
                              ___
                            </span>
                          )}
                        </React.Fragment>
                      ))}
                    </p>
                  </div>
                )}
                
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <input
                    type="text"
                    value={textInputs[currentQuestionIndex] || ''}
                    onChange={(e) => handleTextInputChange(currentQuestionIndex, e.target.value)}
                    disabled={showFeedback[currentQuestionIndex]}
                    placeholder="Digite sua resposta..."
                    className="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 text-base border-2 border-gray-200 dark:border-gray-600 rounded-lg focus:border-purple-500 focus:outline-none dark:bg-gray-700 dark:text-gray-100 disabled:bg-gray-100 dark:disabled:bg-gray-800"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && textInputs[currentQuestionIndex]?.trim()) {
                        handleTextSubmit(currentQuestionIndex);
                      }
                    }}
                  />
                  {!showFeedback[currentQuestionIndex] && (
                    <button
                      onClick={() => handleTextSubmit(currentQuestionIndex)}
                      disabled={!textInputs[currentQuestionIndex]?.trim()}
                      className="w-full sm:w-auto px-6 py-2.5 sm:py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                    >
                      Confirmar
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Questão Dissertativa */}
            {getQuestionType(currentQuestion) === 'essay' && (
              <div className="mb-8">
                <textarea
                  value={textInputs[currentQuestionIndex] || ''}
                  onChange={(e) => handleTextInputChange(currentQuestionIndex, e.target.value)}
                  disabled={showFeedback[currentQuestionIndex]}
                  placeholder="Escreva sua resposta detalhada aqui..."
                  rows={8}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg focus:border-purple-500 focus:outline-none dark:bg-gray-700 dark:text-gray-100 resize-y disabled:bg-gray-100 dark:disabled:bg-gray-800"
                />
                <div className="flex justify-between items-center mt-2">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    <PencilSquareIcon className="h-4 w-4 inline mr-1" />
                    {countWords(textInputs[currentQuestionIndex] || '')} palavras
                    {currentQuestion.minWords && (
                      <span className={countWords(textInputs[currentQuestionIndex] || '') >= currentQuestion.minWords ? 'text-green-600' : 'text-orange-500'}>
                        {' '}(mín: {currentQuestion.minWords})
                      </span>
                    )}
                    {currentQuestion.maxWords && (
                      <span className={countWords(textInputs[currentQuestionIndex] || '') <= currentQuestion.maxWords ? '' : 'text-red-500'}>
                        {' '}(máx: {currentQuestion.maxWords})
                      </span>
                    )}
                  </div>
                  {!showFeedback[currentQuestionIndex] && (
                    <button
                      onClick={() => handleTextSubmit(currentQuestionIndex)}
                      disabled={!textInputs[currentQuestionIndex]?.trim() || 
                        (currentQuestion.minWords ? countWords(textInputs[currentQuestionIndex] || '') < currentQuestion.minWords : false)}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                    >
                      Confirmar Resposta
                    </button>
                  )}
                </div>
                
                {/* Dicas para dissertativas */}
                {currentQuestion.expectedKeyPoints && currentQuestion.expectedKeyPoints.length > 0 && !showFeedback[currentQuestionIndex] && (
                  <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-sm text-blue-700 dark:text-blue-300 font-medium mb-1">
                      💡 Dica: Sua resposta deve abordar:
                    </p>
                    <ul className="text-sm text-blue-600 dark:text-blue-400 list-disc list-inside">
                      {currentQuestion.expectedKeyPoints.slice(0, 3).map((point, idx) => (
                        <li key={idx}>{point}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Feedback and Explanation */}
            {showFeedback[currentQuestionIndex] && answers[currentQuestionIndex] && (
              <div className="mb-8">
                {getQuestionType(currentQuestion) === 'essay' ? (
                  /* Feedback especial para dissertativas - será avaliado no backend */
                  <div className="flex items-center text-blue-600 mb-2">
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    Resposta registrada! Será avaliada pela IA ao finalizar o quiz.
                  </div>
                ) : (getQuestionType(currentQuestion) === 'fill_blank' 
                    ? areAnswersEquivalent(answers[currentQuestionIndex], currentQuestion.correctAnswer)
                    : answers[currentQuestionIndex] === currentQuestion.correctAnswer) ? (
                  <div className="flex items-center text-green-600 mb-2">
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    Resposta correta!
                  </div>
                ) : (
                  <div className="flex items-center text-red-600 mb-2">
                    <XCircleIcon className="h-5 w-5 mr-2" />
                    Resposta incorreta.
                    {getQuestionType(currentQuestion) === 'fill_blank' && (
                      <span className="ml-2 text-gray-600 dark:text-gray-400">
                        A resposta correta é: <strong className="text-green-600">{currentQuestion.correctAnswer}</strong>
                      </span>
                    )}
                  </div>
                )}
                {currentQuestion.explanation && (
                  <div className="mt-2 p-4 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                    <strong>Explicação:</strong>
                    <div>
                      {containsHtml(currentQuestion.explanation)
                        ? <span dangerouslySetInnerHTML={createSafeHtml(currentQuestion.explanation)} />
                        : currentQuestion.explanation}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between gap-2 sm:gap-4 pt-2">
              <button
                onClick={handlePrevQuestion}
                disabled={currentQuestionIndex === 0}
                className="inline-flex items-center px-3 sm:px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeftIcon className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden xs:inline">Anterior</span>
              </button>

              <div className="flex space-x-2 sm:space-x-3">
                {currentQuestionIndex < quiz.questions.length - 1 ? (
                  <button
                    onClick={handleNextQuestion}
                    className="inline-flex items-center px-4 sm:px-5 py-2.5 border border-transparent text-sm font-medium rounded-lg text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors shadow-sm"
                  >
                    Próxima
                    <ChevronRightIcon className="h-4 w-4 ml-1 sm:ml-2" />
                  </button>
                ) : (
                  <button
                    onClick={handleSubmitQuiz}
                    disabled={submitting || getAnsweredQuestionsCount() === 0}
                    className="inline-flex items-center px-4 sm:px-6 py-2.5 border border-transparent text-sm font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        <span className="hidden sm:inline">Enviando...</span>
                        <span className="sm:hidden">...</span>
                      </>
                    ) : (
                      <span>Finalizar</span>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizPlay;